// hooks/useReportingDashboard.js
import { useState, useEffect } from 'react';
import { reportingAPI } from '../APIServiceLayer/APIServiceLayer';
import { getDefaultReportForm, safeJsonParse, getRecipientsArray } from '../utils/reportingUtils';

export const useReportingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Data state
  const [dashboardStats, setDashboardStats] = useState({
    active_reports: 0,
    total_scheduled: 0,
    reports_this_week: 0,
    total_views: 0
  });
  const [scheduledReports, setScheduledReports] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Form state
  const [reportForm, setReportForm] = useState(getDefaultReportForm());

  // Load data on component mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadDashboardData();
      }, 5 * 60 * 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get selectedBusinessId from localStorage
      const selectedBusinessId = localStorage.getItem('selectedBusinessId');
      
      let stats = { active_reports: 0, total_scheduled: 0, reports_this_week: 0, total_views: 0 };
      let scheduled = [];
      let activity = [];

      try {
        stats = await reportingAPI.getDashboardStats();
      } catch (statsError) {
        console.error('Stats error:', statsError);
      }

      try {
        // Pass businessId to filter reports
        scheduled = await reportingAPI.getScheduledReports(selectedBusinessId);
      } catch (scheduledError) {
        console.error('Scheduled reports error:', scheduledError);
      }

      try {
        activity = await reportingAPI.getRecentActivity();
      } catch (activityError) {
        console.error('Recent activity error:', activityError);
      }

      setDashboardStats(stats);
      setScheduledReports(Array.isArray(scheduled) ? scheduled : []);
      setRecentActivity(Array.isArray(activity) ? activity : []);
      setLastRefresh(new Date());
      
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(`Failed to load dashboard data: ${err.message}`);
      
      setDashboardStats({ active_reports: 0, total_scheduled: 0, reports_this_week: 0, total_views: 0 });
      setScheduledReports([]);
      setRecentActivity([]);
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    loading,
    error,
    dashboardStats,
    scheduledReports,
    recentActivity,
    reportForm,
    showCreateModal,
    showEditModal,
    showDeleteConfirm,
    selectedReport,
    lastRefresh,
    autoRefresh,
    
    // Actions
    setError,
    setReportForm,
    setShowCreateModal,
    setShowEditModal,
    setShowDeleteConfirm,
    setSelectedReport,
    setAutoRefresh,
    setScheduledReports,
    setRecentActivity,
    loadDashboardData
  };
};

// Hook for form management
export const useReportForm = (initialForm, onSuccess) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateForm = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateFrequencyConfig = (field, value) => {
    setForm(prev => ({
      ...prev,
      frequency_config: {
        ...prev.frequency_config,
        [field]: value
      }
    }));
  };

  const addRecipient = () => {
    setForm(prev => ({
      ...prev,
      recipients: [...prev.recipients, '']
    }));
  };

  const removeRecipient = (index) => {
    setForm(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };

  const updateRecipient = (index, email) => {
    setForm(prev => ({
      ...prev,
      recipients: prev.recipients.map((recipient, i) => 
        i === index ? email : recipient
      )
    }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setError(null);
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      setError('Report name is required');
      return false;
    }
    
    if (form.recipients.filter(email => email.trim()).length === 0) {
      setError('At least one recipient is required');
      return false;
    }

    return true;
  };

  const submitForm = async (submitFunction) => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);
      
      const reportData = {
        ...form,
        recipients: form.recipients.filter(email => email.trim())
      };
      
      await submitFunction(reportData);
      resetForm();
      onSuccess();
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  return {
    form,
    loading,
    error,
    updateForm,
    updateFrequencyConfig,
    addRecipient,
    removeRecipient,
    updateRecipient,
    resetForm,
    submitForm,
    setForm
  };
};